'use client';

/**
 * ObeyaBoardCard — full-bleed kanban board (RFQs/POs by stage).
 *
 * Wraps the existing `<ObeyaKanban />` so it paints from the
 * cards-everywhere LayoutEngine on `mode1`. Same data the legacy
 * obeya widget consumes (`useObeyaProjectData` → `obeya.detail`),
 * same React Query cache — switching to this preset doesn't refetch
 * if the canvas already has the data warm.
 *
 * Designed to be placed full-bleed (`x=0, y=0, w=12, h=12`) — the
 * kanban draws its own header / column scroll and assumes it owns
 * the canvas viewport.
 *
 * @see klyde_edge/composition/cards/obeya.py — content_key="obeya_board"
 * @see klyde_ipe/components/widgets/obeya-kanban.tsx
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { ObeyaKanban } from './obeya-kanban';
import { surfaceBase } from '@/lib/design/tokens.x';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useObeyaProjectData } from './use-obeya-project-data';

export function ObeyaBoardCard() {
  const { data, isLoading, projectId, queryKey } = useObeyaProjectData();
  const { contentKey } = useCardIdentity();

  // Scene export — register the kanban board into the capture envelope.
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_board',
    status: (data ? 'loaded' : projectId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: projectId ? queryKey : null,
    text: data ? `${(data.rfqs ?? []).length} RFQs · ${(data.pos ?? []).length} POs` : 'Board',
    json: data
      ? { project_id: projectId ?? null, rfq_count: (data.rfqs ?? []).length, po_count: (data.pos ?? []).length }
      : { project_id: projectId ?? null },
  }), [contentKey, data, projectId, queryKey]));

  if (!projectId) return <Empty msg="Select a project" />;
  if (isLoading || !data) return <Empty msg="Loading board…" />;

  return (
    <div className="h-full w-full overflow-hidden" style={{ fontSize: surfaceBase.canvas }}>
      <ObeyaKanban
        projectId={projectId}
        rfqs={data.rfqs ?? []}
        pos={data.pos ?? []}
      />
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
      {msg}
    </div>
  );
}
